import React, { useEffect, useState } from "react";
import './Dashboard.scss'
import axios from "axios";
import Layout from "../components/Layout";

interface FileNode {
    name: string;
    type: "folder" | "file";
    dateModified: string;
    children?: FileNode[];
    url: string;
  }

let fileStructure: FileNode[] = [];

const API_URL = 'https://www.theprojectgenesis.net'; // 'https://theprojectgenesis.net';

  
const Dashboard: React.FC = () => {
    const [totalPath, setTotalPath] = useState<FileNode[]>([]);
  const [currentPath, setCurrentPath] = useState<FileNode[]>([]);

  const fetchFiles = async () => {
    const token = localStorage.getItem("authToken") || '';
  
    const response = await fetch(API_URL + "/api/files", {
      method: "GET",
      headers: { 'Authorization': `Bearer ${token}` }
    });
  
    if (response.ok) {
      const files = await response.json();

      setTotalPath(files.children);
      setCurrentPath(files.children);
    } else {
      const error = await response.json();
      alert(error.error);
    }
  };

  useEffect(()=> {
    fetchFiles();
  }, []);

  const handleFolderClick = (folder: FileNode) => {
    if (folder.children) setCurrentPath(folder.children);
  };

  const handleBackClick = () => {
    // Handle navigation back to the parent folder (simplified)
    setCurrentPath(totalPath);
  };

  const handleDownload = (filePath: string) => {
    const token = localStorage.getItem("authToken") || '';
    const url = API_URL +`/api/download?path=${filePath}`;
    fetch(url, { headers: { Authorization: `${token}` } })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        return response.blob();
      })
      .then((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filePath.split("/").pop() || "file";
        a.click();
      })
      .catch((error) => console.error("Error downloading file:", error));
  };

  const [selectedFile, setSelectedFile] = useState(Object);
  const [isViewing, setIsViewing] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  const handleView = (file: any) => {
    setSelectedFile(file);
    setIsViewing(true);
  };

  const getFilePreview = (file: any) => {
    const extension = file.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        // Render image
        return <img src={API_URL + `/files/${file}`} alt={file} className="preview-image" />;
    }

    if (extension === 'pdf') {
        // Render PDF
        return (
            <iframe
                src={API_URL + `/files/${file}`}
                title="PDF Preview"
                className="preview-pdf"
                frameBorder="0"
            />
        );
    }

    if (extension === 'ini') {
        // Render .ini as text
        return (
            <div className="text-preview">
                <p>
                    Preview of <b>{file.name}</b>:
                </p>
                <iframe
                    src={API_URL + `/files/${file}`}
                    title="INI Preview"
                    frameBorder="0"
                    className="preview-text"
                />
            </div>
        );
    }

    // Default case for unsupported file types
    return <p>Preview not available for this file type.</p>;
  };

  const handleRename = (file: any) => {
      setSelectedFile(file);
      setNewName(file.name); // Pre-fill with the current name
      setIsRenaming(true);
  };

  const saveRename = async () => {
      if (newName.trim()) {
          console.log("Renamed:", selectedFile.name, "to", newName);
          
          await renameFileOrFolder(selectedFile.url, selectedFile.url.substring(0, selectedFile.url.lastIndexOf('/')) + '/' + newName);
          // Here, call the API to rename the file/folder
          setSelectedFile({ ...selectedFile, name: newName });
          setIsRenaming(false);
          setNewName("");
      }
  };

  const renameFileOrFolder = async (oldPath: string, newPath: string) => {
    try {
        const response = await axios.post(API_URL + "/api/rename", {
            oldPath,
            newPath,
        });
        await fetchFiles();
    } catch (error) {
        console.log(error)
    }
  };

  const generateFolderName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `Folder_${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
};

  const createNewFolder = async () => {
      let currentURL = "";
      if (currentPath.length > 0) {
        let currentfolder = currentPath[0];
        currentURL = currentfolder.url.substring(0, currentfolder.url.lastIndexOf('/'))
      }

      currentURL += "/" + generateFolderName();
      try {
          const response = await axios.post(API_URL + "/api/create-folder", {
            currentURL,
          });
          await fetchFiles();
      } catch (err) {
        console.log(err)
      }
  };

  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleFileChange = (e: any) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    handleUpload(selectedFile);
  };

  // Trigger file selection dialog
  const handleButtonClick = () => {
    document.getElementById('fileInput')?.click();
  };

  const handleUpload = async (selectedFile: any) => {
    if (!selectedFile) {
        alert("Please select a file first");
        return;
    }

    let currentURL = "";
    if (currentPath.length > 0) {
      let currentfolder = currentPath[0];
      currentURL = currentfolder.url.substring(0, currentfolder.url.lastIndexOf('/'))
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('folder', currentURL); // Add folder name here

    setUploadStatus('Uploading...');

    try {
        const response = await axios.post(API_URL + '/api/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-Folder-Name': currentURL,
            }
        });
        // alert('File uploaded successfully: ' + response.data.filePath);
        setUploadStatus(`File uploaded successfully: ${response.data.filePath}`);

        await fetchFiles();

    } catch (error) {
        console.error('Error uploading file:', error);
        setUploadStatus('Failed to upload file');
    }
};


  return (
    <Layout>
    <div>
      {currentPath !== totalPath && (
        <button onClick={handleBackClick} className="back-button">Back</button>
      )}
        {currentPath.map((item, index) => (
            <div key={index} className="file-item" onClick={() => handleFolderClick(item)}>
                <div className="file-icon">
                    {item.type === "folder" ? (
                    <span role="img" aria-label="folder">
                        📁
                    </span>
                    ) : (
                    <span role="img" aria-label="file">
                        📄
                    </span>
                    )}
                </div>
                
                <div className="file-details">
                    <div className="file-name">
                        
                        {
                            item.type === "file" ?
                            <a href={API_URL + '/files' + item.url} target="_blank" rel="noopener noreferrer">{item.name}</a>
                            :
                            <span>{item.name}</span>
                        }
                    </div>
                    <div className="file-meta">
                        <span className="file-type">{item.type}</span>
                        {
                        item.type === "file" ?
                            (
                              <>
                              <button onClick={() => handleView(item.url || '')} style={{backgroundColor: 'transparent', border: 'none'}}>View</button>
                              <button onClick={() => handleRename(item)} style={{backgroundColor: 'transparent', border: 'none'}}>Rename</button>
                              <button onClick={() => handleDownload(item.url || '')} style={{backgroundColor: 'transparent', border: 'none'}}>Download</button>
                              </>
                            )
                        :
                        <>
                          <button onClick={() => handleRename(item)} style={{backgroundColor: 'transparent', border: 'none'}}>Rename</button>
                        </>
                        }
                        <span className="file-date">{item.dateModified}</span>
                    </div>
                </div>
            </div>
        ))}

        {/* View Modal */}
        {isViewing && selectedFile && (
            <div className="modal">
              <div className="modal-content">
                  {/* <h2>Viewing: {selectedFile.name}</h2> */}
                  <div className="preview-area">
                      {getFilePreview(selectedFile)}
                  </div>
                  <button onClick={() => setIsViewing(false)} style={{color: 'black', position: 'absolute', top: '20px', right: '20px', fontSize: '30px', cursor: 'pointer'}}>X</button>
              </div>
          </div>
        )}

        {/* Rename Modal */}
        {isRenaming && selectedFile && (
            <div className="modal">
                <div className="modal-content">
                    <h2>Renaming: {selectedFile.name}</h2>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Enter new name"
                    />
                    <div className="modal-actions">
                        <button onClick={saveRename}>Save</button>
                        <button onClick={() => setIsRenaming(false)} style={{marginLeft: '10px'}}>Cancel</button>
                    </div>
                </div>
            </div>
        )}

        <button onClick={() => createNewFolder()} style={{backgroundColor: 'transparent', border: 'none', position: 'absolute', left: '20px', bottom: '30px', fontSize: '20px'}}>+ Folder</button>
        <input
                id="fileInput"
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
          {/* Display upload status */}
          {uploadStatus && <p>{uploadStatus}</p>}
        <button onClick={() => handleButtonClick()} style={{backgroundColor: 'transparent', border: 'none', position: 'absolute', right: '20px', bottom: '30px', fontSize: '20px'}}>+ File</button>
    </div>
    </Layout>
  );
};

export default Dashboard;
